import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Button,
    Table,
    Switch,
    Card,
    Row,
    Col,
    Typography,
    message,
    Alert,
    Tag,
    Space,
    Modal,
    Divider,
    Statistic,
    Progress,
    Tooltip,
    Radio,
    Select
} from 'antd';
import {
    SettingOutlined,
    InfoCircleOutlined,
    ReloadOutlined,
    QuestionCircleOutlined
} from '@ant-design/icons';
import API from '../services/api';
import usePermissionCheck from '../hooks/usePermissionCheck';
import PermissionDenied from '../components/PermissionDenied';

const { Title, Text } = Typography;
const { TextArea } = Input;

const UserApplicationRules = () => {
    const { hasPermission, isLoading: permissionLoading, userAccountType } = usePermissionCheck('canManageIPs');

    const [portForm] = Form.useForm();

    // 数据状态
    const [hostIp, setHostIp] = useState(undefined);
    const [rules, setRules] = useState([]);
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [switchLoading, setSwitchLoading] = useState({});
    const [assignedIps, setAssignedIps] = useState([]);
    const [ipLoading, setIpLoading] = useState(false);

    // 分页状态
    const [pageSize, setPageSize] = useState(10);
    const [current, setCurrent] = useState(1);

    // 模态框状态
    const [portModalVisible, setPortModalVisible] = useState(false);
    const [helpModalVisible, setHelpModalVisible] = useState(false);
    const [selectedRule, setSelectedRule] = useState(null);

    // IP流量直通状态
    const [ipPassThroughStatus, setIpPassThroughStatus] = useState({});

    // 组件挂载时获取IP列表
    useEffect(() => {
        if (hasPermission) {
            fetchAssignedIps();
        }
    }, [hasPermission]);

    // 权限检查
    if (!hasPermission) {
        return (
            <PermissionDenied
                isLoading={permissionLoading}
                permissionName="IP策略"
                userAccountType={userAccountType}
            />
        );
    }

    // 获取用户分配的IP列表
    const fetchAssignedIps = async (search = '') => {
        setIpLoading(true);
        try {
            const response = await API.get('/api/user/assigned-ips', {
                params: { search: search }
            });

            if (response.data.assignedIps) {
                // 确保正确处理IP数据结构，无论是字符串数组还是对象数组
                const ips = response.data.assignedIps.map(item => {
                    // 如果是对象，提取ip字段；如果是字符串，直接返回
                    if (typeof item === 'object' && item !== null) {
                        return item.ip || item;
                    }
                    return item;
                });
                setAssignedIps(ips);
            } else {
                message.error('获取IP列表失败');
                setAssignedIps([]);
            }
        } catch (error) {
            console.error('获取IP列表失败:', error);
            if (error.response?.data?.message) {
                message.error(error.response.data.message);
            } else {
                message.error('获取IP列表失败');
            }
            setAssignedIps([]);
        } finally {
            setIpLoading(false);
        }
    };

    // 获取应用规则列表
    const fetchRules = async (ip) => {
        if (!ip) {
            message.warning('请输入IP地址');
            return;
        }

        setLoading(true);
        try {
            const response = await API.get('/api/user/apply-rules', {
                params: { ip: ip }
            });

            if (response.data.code === 0) {
                setRules(response.data.data.list);
                setUsage(response.data.data);

                // 使用API返回的pass_through字段更新流量直通状态
                if (response.data.data.pass_through !== undefined) {
                    setIpPassThroughStatus(prev => ({
                        ...prev,
                        [ip]: response.data.data.pass_through
                    }));
                }
            } else {
                message.error(response.data.msg || '获取应用规则失败');
            }
        } catch (error) {
            console.error('获取应用规则失败:', error);
            if (error.response?.data?.msg) {
                message.error(error.response.data.msg);
            } else {
                message.error('获取应用规则失败');
            }
        } finally {
            setLoading(false);
        }
    };

    // 开启/禁用规则
    const handleStatusChange = async (ruleId, checked) => {
        setSwitchLoading(prev => ({ ...prev, [ruleId]: true }));

        try {
            const response = await API.put('/api/user/apply-rule-status', {
                id: ruleId,
                ip: hostIp,
                status: checked ? 1 : 0
            });

            if (response.data.code === 0) {
                message.success(`规则${checked ? '开启' : '禁用'}成功`);
                // 刷新规则列表
                fetchRules(hostIp);
            } else {
                // 处理特殊错误情况
                if (response.data.msg?.includes('已达到单IP最大应用规则数量限制')) {
                    message.error({
                        content: response.data.msg,
                        duration: 6,
                        style: { marginTop: '20px' }
                    });
                } else {
                    message.error(response.data.msg || '操作失败');
                }
            }
        } catch (error) {
            console.error('修改规则状态失败:', error);
            // 检查是否是API返回的具体错误信息
            if (error.response?.data?.msg) {
                if (error.response.data.msg.includes('已达到单IP最大应用规则数量限制')) {
                    message.error({
                        content: error.response.data.msg,
                        duration: 6,
                        style: { marginTop: '20px' }
                    });
                } else {
                    message.error(error.response.data.msg);
                }
            } else {
                message.error('修改规则状态失败');
            }
        } finally {
            setSwitchLoading(prev => ({ ...prev, [ruleId]: false }));
        }
    };

    // 控制流量直通（针对整个IP）
    /*const handlePassThroughToggle = async () => {
        if (!hostIp) {
            message.warning('请先选择IP地址');
            return;
        }

        if (!usage || !usage.apply_rule_id) {
            message.warning('未获取到apply_rule_id，无法设置流量直通');
            return;
        }

        setSwitchLoading(prev => ({ ...prev, [`passthrough_${hostIp}`]: true }));

        try {
            const currentPassThrough = ipPassThroughStatus[hostIp] || 0;
            const newPassThrough = currentPassThrough === 1 ? 0 : 1;
            // 使用查询返回数据中的apply_rule_id
            const applyRuleId = usage.apply_rule_id;

            const response = await API.put('/api/user/apply-rule-pass-through', {
                ip: hostIp,
                id: applyRuleId, // 使用data中的apply_rule_id
                pass_through: newPassThrough
            });

            if (response.data.success) {
                message.success(response.data.message || `流量直通功能已${newPassThrough === 1 ? '开启' : '关闭'}`);
                // 使用API返回的实际状态更新IP流量直通状态
                const actualPassThrough = response.data.data?.pass_through !== undefined ?
                    response.data.data.pass_through : newPassThrough;
                setIpPassThroughStatus(prev => ({
                    ...prev,
                    [hostIp]: actualPassThrough
                }));
            } else {
                message.error(response.data.message || '修改流量直通设置失败');
            }
        } catch (error) {
            console.error('修改流量直通设置失败:', error);
            if (error.response?.data?.message) {
                message.error(error.response.data.message);
            } else {
                message.error('修改流量直通设置失败');
            }
        } finally {
            setSwitchLoading(prev => ({ ...prev, [`passthrough_${hostIp}`]: false }));
        }
    };*/

    // 配置端口
    const handlePortConfig = async (values) => {
        try {
            let ports = []; // 默认为空数组，表示使用默认端口

            // 如果用户明确选择了端口配置选项
            if (values.portConfigType === 'custom') {
                if (values.customPorts && values.customPorts.trim()) {
                    // 解析端口配置，格式：111,222-333,444
                    const portRanges = values.customPorts.split(',').map(range => {
                        const trimmed = range.trim();
                        if (trimmed.includes('-')) {
                            const [start, end] = trimmed.split('-').map(p => parseInt(p.trim()));
                            return [start, end];
                        } else {
                            const port = parseInt(trimmed);
                            return [port, port];
                        }
                    });
                    ports = portRanges;
                } else {
                    // 用户选择了自定义但没输入内容，使用默认端口
                    ports = [];
                }
            } else {
                // 默认模式或清除模式都使用空数组
                ports = [];
            }

            const requestData = {
                id: selectedRule.id,
                ip: hostIp,
                ports: ports
            };

            const response = await API.put('/api/user/apply-rule-with-ports', requestData);

            if (response.data.code === 0) {
                message.success('规则配置成功');
                setPortModalVisible(false);
                portForm.resetFields();
                setSelectedRule(null);
                // 刷新规则列表
                fetchRules(hostIp);
            } else {
                // 处理特殊错误情况
                if (response.data.msg?.includes('已达到单IP最大应用规则数量限制')) {
                    message.error({
                        content: response.data.msg,
                        duration: 6,
                        style: { marginTop: '20px' }
                    });
                } else {
                    message.error(response.data.msg || '配置失败');
                }
            }
        } catch (error) {
            console.error('配置规则失败:', error);
            // 检查是否是API返回的具体错误信息
            if (error.response?.data?.msg) {
                if (error.response.data.msg.includes('已达到单IP最大应用规则数量限制')) {
                    message.error({
                        content: error.response.data.msg,
                        duration: 6,
                        style: { marginTop: '20px' }
                    });
                } else {
                    message.error(error.response.data.msg);
                }
            } else {
                message.error('配置规则失败');
            }
        }
    };



    // 刷新数据
    const handleRefresh = () => {
        if (hostIp) {
            fetchRules(hostIp);
        }
    };

    // 显示端口配置模态框
    const showPortModal = (rule) => {
        setSelectedRule(rule);
        setPortModalVisible(true);
        // 重置表单到默认状态
        portForm.setFieldsValue({
            portConfigType: 'default',
            customPorts: undefined
        });
    };

    // 表格列定义
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60,
        },
        {
            title: '规则名称',
            dataIndex: 'name',
            key: 'name',
            width: 150,
            render: (text) => (
                <Text
                    strong
                    style={{
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}
                >
                    {text}
                </Text>
            ),
        },
        {
            title: '规则说明',
            dataIndex: 'description',
            key: 'description',
            width: 100,
            render: (text) => {
                if (!text) return <Text type="secondary">-</Text>;

                const truncatedText = text.length > 30 ? `${text.substring(0, 30)}...` : text;

                return (
                    <Tooltip title={text} placement="top">
                        <Text
                            type="secondary"
                            style={{
                                fontSize: '12px',
                                cursor: 'pointer',
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                        >
                            {truncatedText}
                        </Text>
                    </Tooltip>
                );
            },
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status, record) => {
                const applyRuleId = record.apply_rule_id || record.id; // 使用apply_rule_id，fallback到id
                return (
                    <Switch
                        checked={status === 1}
                        loading={switchLoading[applyRuleId]}
                        onChange={(checked) => handleStatusChange(applyRuleId, checked)}
                    />
                );
            },
        },
        {
            title: '端口配置',
            dataIndex: 'port_list',
            key: 'port_list',
            width: 100,
            render: (portList, record) => {
                if (!portList || portList.length === 0) {
                    return <Tag color="default">默认端口</Tag>;
                }

                const portText = portList.join(', ');
                const isLong = portText.length > 10;
                const displayText = isLong ? `${portText.substring(0, 10)}...` : portText;

                return (
                    <Tooltip title={portText} placement="top">
                        <Tag
                            color="blue"
                            style={{
                                cursor: 'pointer',
                                maxWidth: '90px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'inline-block'
                            }}
                        >
                            {displayText}
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: '默认端口',
            dataIndex: 'default_port',
            key: 'default_port',
            width: 100,
            render: (port) => {
                if (!port) return <Tag color="green">-</Tag>;

                const portText = port.toString();
                const isLong = portText.length > 8;
                const displayText = isLong ? `${portText.substring(0, 8)}...` : portText;

                return (
                    <Tooltip title={portText} placement="top">
                        <Tag
                            color="green"
                            style={{
                                cursor: 'pointer',
                                maxWidth: '70px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'inline-block'
                            }}
                        >
                            {displayText}
                        </Tag>
                    </Tooltip>
                );
            },
        },

        {
            title: '操作',
            key: 'actions',
            width: 120,
            render: (_, record) => {
                const isDisabled = record.status === 1 || (usage && usage.app_enabled_count >= usage.app_max);
                const tooltipTitle = record.status === 1
                    ? "规则已开启，无需配置"
                    : (usage && usage.app_enabled_count >= usage.app_max)
                        ? "已达到规则数量上限，请先禁用其他规则"
                        : "配置端口并开启";

                return (
                    <Space>
                        <Button
                            type="text"
                            icon={<SettingOutlined />}
                            onClick={() => showPortModal(record)}
                            disabled={isDisabled}
                            title={tooltipTitle}
                        />
                    </Space>
                );
            },
        },
    ];

    return (
        <div style={{ padding: '24px', background: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <Title level={2} style={{ marginBottom: '24px' }}>
                    应用规则管理
                </Title>

                {/* 搜索区域 */}
                <Card style={{ marginBottom: '24px' }}>
                    <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>IP地址：</span>
                            <Select
                                placeholder="请选择IP地址"
                                style={{ width: '300px' }}
                                value={hostIp}
                                onChange={(value) => {
                                    setHostIp(value);
                                    setCurrent(1); // 切换IP时回到第一页
                                    if (value) {
                                        fetchRules(value);
                                    } else {
                                        setRules([]);
                                        setUsage(null);
                                    }
                                }}
                                loading={ipLoading}
                                showSearch
                                filterOption={false}
                                onSearch={(value) => fetchAssignedIps(value)}
                                notFoundContent={ipLoading ? '加载中...' : '暂无可用IP'}
                                allowClear
                            >
                                {assignedIps.map(ip => (
                                    <Select.Option key={ip} value={ip}>
                                        {ip}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                        <Space>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={handleRefresh}
                                disabled={!hostIp}
                                loading={loading}
                            >
                                刷新规则
                            </Button>
                            <Button
                                icon={<QuestionCircleOutlined />}
                                onClick={() => setHelpModalVisible(true)}
                            >
                                使用帮助
                            </Button>
                        </Space>
                    </div>

                    {/* 使用统计 */}
                    {usage && (
                        <div>
                            <Divider />
                            <Row gutter={16}>
                                <Col span={6}>
                                    <Statistic
                                        title="总规则数"
                                        value={usage.count}
                                        prefix={<InfoCircleOutlined />}
                                    />
                                </Col>
                                <Col span={6}>
                                    <Statistic
                                        title="已开启"
                                        value={usage.app_enabled_count}
                                        valueStyle={{ color: '#52c41a' }}
                                    />
                                </Col>
                                <Col span={6}>
                                    <Statistic
                                        title="最大限制"
                                        value={usage.app_max}
                                        valueStyle={{ color: '#1890ff' }}
                                    />
                                </Col>
                                <Col span={6}>
                                    <div>
                                        <div style={{ marginBottom: '8px' }}>使用率</div>
                                        <Progress
                                            percent={Math.round((usage.app_enabled_count / usage.app_max) * 100)}
                                            status={usage.app_enabled_count >= usage.app_max ? 'exception' : 'active'}
                                            strokeColor={usage.app_enabled_count >= usage.app_max ? '#ff4d4f' : '#52c41a'}
                                        />
                                        <Text type="secondary">
                                            {usage.usage_info.usage_rate}
                                        </Text>
                                    </div>
                                </Col>
                            </Row>

                            {/* 限制提示 */}
                            {usage.app_enabled_count >= usage.app_max && (
                                <Alert
                                    message="规则数量已达上限"
                                    description={`当前IP已达到最大应用规则数量限制（${usage.app_max}个），无法开启更多规则。请先禁用其他规则后再开启新规则。`}
                                    type="warning"
                                    showIcon
                                    style={{ marginTop: '16px' }}
                                />
                            )}
                            {usage.app_enabled_count >= usage.app_max * 0.8 && usage.app_enabled_count < usage.app_max && (
                                <Alert
                                    message="接近规则数量上限"
                                    description={`当前已开启 ${usage.app_enabled_count} 个规则，接近最大限制 ${usage.app_max} 个。建议及时清理不需要的规则。`}
                                    type="info"
                                    showIcon
                                    style={{ marginTop: '16px' }}
                                />
                            )}
                        </div>
                    )}
                </Card>

                {/* 规则列表 */}
                {hostIp && (
                    <Card>
                        <div style={{ marginBottom: '16px' }}>
                            <Title level={4} style={{ margin: 0, marginBottom: '16px' }}>
                                规则列表 - {hostIp}
                            </Title>
                            {usage && usage.app_enabled_count >= usage.app_max && (
                                <Alert
                                    message="已达到最大规则开启数量限制"
                                    description="当前无法开启新规则，请先禁用其他规则后再试"
                                    type="warning"
                                    showIcon
                                    style={{ marginBottom: '16px' }}
                                />
                            )}

                            {/* 流量直通功能区域 */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '16px',
                                padding: '12px',
                                background: '#fafafa',
                                borderRadius: '6px',
                                border: '1px solid #d9d9d9'
                            }}>
                                <div>
                                    <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                                        流量直通功能
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                        让此IP的流量绕过防护检测，请谨慎使用
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Tooltip title="该功能需要联系运维团队申请开通权限">
                                        <Switch
                                            size="small"
                                            checked={ipPassThroughStatus[hostIp] === 1}
                                            loading={switchLoading[`passthrough_${hostIp}`]}
                                            disabled={true} // 暂时禁用，需要申请权限
                                        />
                                    </Tooltip>
                                    <Tag
                                        color={ipPassThroughStatus[hostIp] === 1 ? 'green' : 'default'}
                                        style={{
                                            fontSize: '11px'
                                        }}
                                    >
                                        {ipPassThroughStatus[hostIp] === 1 ? '已开启' : '未开启'}
                                    </Tag>
                                </div>
                            </div>
                        </div>

                        <Table
                            columns={columns}
                            dataSource={rules}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                current: current,
                                pageSize: pageSize,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                                onChange: (page, size) => {
                                    setCurrent(page);
                                    setPageSize(size);
                                },
                                onShowSizeChange: (current, size) => {
                                    setCurrent(1); // 切换每页数量时回到第一页
                                    setPageSize(size);
                                },
                                pageSizeOptions: ['10', '20', '50', '100'],
                            }}
                            scroll={{ x: 1100 }}
                        />
                    </Card>
                )}

                {/* 端口配置模态框 */}
                <Modal
                    title={`配置规则端口 - ${selectedRule?.name}`}
                    open={portModalVisible}
                    onCancel={() => {
                        setPortModalVisible(false);
                        portForm.resetFields();
                        setSelectedRule(null);
                    }}
                    onOk={() => portForm.submit()}
                    okText="应用配置"
                >
                    <Form
                        form={portForm}
                        layout="vertical"
                        onFinish={handlePortConfig}
                        initialValues={{
                            portConfigType: 'default'
                        }}
                    >
                        <Alert
                            message="端口配置说明"
                            description="选择端口配置方式。自定义格式：111,222-333,444（支持单个端口和端口范围），写0表示全部端口"
                            type="info"
                            showIcon
                            style={{ marginBottom: '16px' }}
                        />

                        <Form.Item
                            label="端口配置方式"
                            name="portConfigType"
                            rules={[{ required: true, message: '请选择端口配置方式' }]}
                        >
                            <Radio.Group>
                                <Radio value="default">
                                    <div>
                                        <div>使用默认端口</div>
                                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                            应用规则的默认端口配置
                                        </div>
                                    </div>
                                </Radio>
                                <Radio value="custom">
                                    <div>
                                        <div>自定义端口配置</div>
                                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                            指定特定的端口或端口范围
                                        </div>
                                    </div>
                                </Radio>
                            </Radio.Group>
                        </Form.Item>

                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) =>
                                prevValues.portConfigType !== currentValues.portConfigType
                            }
                        >
                            {({ getFieldValue }) => {
                                return getFieldValue('portConfigType') === 'custom' ? (
                                    <Form.Item
                                        label="自定义端口配置"
                                        name="customPorts"
                                    >
                                        <TextArea
                                            rows={3}
                                            placeholder="例如：111,222-333,444"
                                        />
                                    </Form.Item>
                                ) : null;
                            }}
                        </Form.Item>
                    </Form>
                </Modal>

                {/* 使用帮助模态框 */}
                <Modal
                    title="使用帮助"
                    open={helpModalVisible}
                    onCancel={() => setHelpModalVisible(false)}
                    footer={[
                        <Button key="close" onClick={() => setHelpModalVisible(false)}>
                            关闭
                        </Button>
                    ]}
                    width={700}
                    centered
                >
                    <div>
                        <Title level={4}>功能说明</Title>
                        <p>应用规则管理功能用于为指定IP地址配置和管理防护规则。</p>

                        <Title level={4}>使用步骤</Title>
                        <ol>
                            <li><strong>选择IP地址：</strong>在下拉框中选择要管理的IP地址，支持搜索过滤功能，选择后将自动加载规则列表</li>
                            <li><strong>配置规则：</strong>点击规则操作列的设置图标，配置端口并开启规则</li>
                            <li><strong>管理状态：</strong>使用状态开关可以快速开启或禁用已配置的规则</li>
                            <li><strong>刷新数据：</strong>如需更新规则状态，可点击"刷新规则"按钮</li>
                        </ol>

                        <Title level={4}>端口配置说明</Title>
                        <ul>
                            <li><strong>使用默认端口：</strong>应用规则定义的默认端口配置</li>
                            <li><strong>自定义端口配置：</strong>指定特定的端口或端口范围
                                <ul>
                                    <li>单个端口：111</li>
                                    <li>端口范围：222-333</li>
                                    <li>多个配置：111,222-333,444</li>
                                    <li>全部端口：0</li>
                                </ul>
                            </li>
                        </ul>

                        <Title level={4}>使用限制</Title>
                        <ul>
                            <li>每个IP地址有最大规则开启数量限制</li>
                            <li>达到限制后需要先禁用其他规则才能开启新规则</li>
                            <li>系统会实时显示当前使用情况和限制信息</li>
                        </ul>

                        <Title level={4}>状态说明</Title>
                        <ul>
                            <li><strong>开启状态：</strong>规则正在生效，提供防护功能</li>
                            <li><strong>禁用状态：</strong>规则未生效，不提供防护功能</li>
                            <li><strong>默认端口：</strong>使用规则预设的端口配置</li>
                            <li><strong>自定义端口：</strong>使用用户配置的特定端口</li>
                            <li><strong>流量直通：</strong>让整个IP的流量绕过防护检测（请谨慎使用）</li>
                        </ul>

                        <Title level={4}>流量直通功能</Title>
                        <Alert
                            message="流量直通功能申请"
                            description={
                                <div>
                                    <div>流量直通功能是针对整个IP地址的全局开关。此功能可以让指定IP的所有流量绕过防护检测，但会降低安全防护级别，请根据实际业务需求谨慎使用。</div>
                                    <div style={{ marginTop: '8px' }}>
                                        <strong>使用提醒：</strong>开启流量直通后，该IP的所有流量将绕过防护检测，可能增加安全风险。建议仅在必要时使用，并及时关闭。
                                    </div>
                                </div>
                            }
                            type="info"
                            showIcon
                            style={{ marginBottom: '16px' }}
                        />

                        <Alert
                            message="注意事项"
                            description="规则配置后需要一定时间生效，建议配置完成后等待1-2分钟再进行测试。若不会配置请联系服务商进行咨询，否则可能造成无法访问的情况。如遇问题请联系管理员。"
                            type="warning"
                            showIcon
                            style={{ marginTop: '16px' }}
                        />
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default UserApplicationRules; 